import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { previewPayroll, approvePayroll, updatePayroll } from 'services/payrolls';
import { getTimeOffByPeriod, runAccruals, markRequestPaid, getBalances, getHolidays, createNotification } from 'services/timeOff';
import { getContractors } from 'services/contractors';
import { getEmployees } from 'services/employees';
import { getEarningCodes } from 'services/earningCodes';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../axios/axiosInstance';

// Map time off policy type to adjustment field
const typeToField = (type = '') => {
    const t = type.toLowerCase();
    if (t === 'sick') return 'sickHours';
    if (t === 'holiday') return 'holidayHours';
    return 'ptoHours';
};

const companyId = () => localStorage.getItem('companyId') || '';

const fmt = (n) =>
    n == null ? '-' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const normalizePayMethod = (val) => {
    if (!val) return '';
    const v = val.toLowerCase().replace(/[\s_-]/g, '');
    if (v === 'directdeposit') return 'direct_deposit';
    if (v === 'check') return 'check';
    if (v === 'manual') return 'manual';
    return '';
};

const numInput = (val, onChange, placeholder = '0') => (
    <input
        type="number"
        value={val ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
        placeholder={placeholder}
        style={{ width: 80, padding: '2px 6px', fontSize: 12, border: '1px solid #ced4da', borderRadius: 4, textAlign: 'right' }}
    />
);

const PayrollPreviewModal = ({ payroll, onClose, onApproved }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [approved, setApproved] = useState(false);
    const [adjustments, setAdjustments] = useState({});
    const [contAdjustments, setContAdjustments] = useState({});
    // Tracks which contractors are included in the payroll (additive model)
    const [includedContractorIds, setIncludedContractorIds] = useState(new Set());
    // Tracks which employees are included in the payroll
    const [includedEmployeeIds, setIncludedEmployeeIds] = useState(new Set());
    const [showAddContractor, setShowAddContractor] = useState(false);
    const [showAdj, setShowAdj] = useState(true);
    const [adjError, setAdjError] = useState('');
    const [adjSuccess, setAdjSuccess] = useState(false);
    const [approveError, setApproveError] = useState('');
    const [bulkHolidayHours, setBulkHolidayHours] = useState('8');
    const [bulkHolidayTarget, setBulkHolidayTarget] = useState('salaried');
    // Track which employees have been initialized so recalculate doesn't double-count hours
    const initializedEmpRef = useRef(new Set());
    // Auto-recalculate once when unpaid hours are first detected
    const autoRecalcFiredRef = useRef(false);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['payroll-preview', payroll.id],
        queryFn: () => previewPayroll(payroll.id),
        staleTime: 0,
        refetchOnMount: 'always',
    });

    const calc = data;

    const startDate = payroll.startDate?.slice(0, 10);
    const endDate   = payroll.endDate?.slice(0, 10);
    const { data: timeOffRaw } = useQuery({
        queryKey: ['time-off-by-period', startDate, endDate],
        queryFn: () => getTimeOffByPeriod({ startDate, endDate }),
        enabled: !!startDate && !!endDate,
        staleTime: 0,
    });

    const { data: timeOffBalancesRaw = [] } = useQuery({
        queryKey: ['payroll-preview-balances'],
        queryFn: () => getBalances(),
        staleTime: 60 * 1000,
    });

    const { data: companyHolidays = [] } = useQuery({
        queryKey: ['company-holidays'],
        queryFn: getHolidays,
        staleTime: 5 * 60 * 1000,
    });

    const { data: employeesRaw = [] } = useQuery({
        queryKey: ['employees'],
        queryFn: getEmployees,
        staleTime: 5 * 60 * 1000,
    });

    const { data: earningCodesRaw = [] } = useQuery({
        queryKey: ['earning-codes'],
        queryFn: getEarningCodes,
        staleTime: 5 * 60 * 1000,
    });
    const earningCodes = Array.isArray(earningCodesRaw) ? earningCodesRaw : [];

    // Map employeeId → default payment method from employee profile
    const employeePayMethodMap = useMemo(() => {
        const arr = Array.isArray(employeesRaw) ? employeesRaw : (employeesRaw?.data ?? []);
        const map = {};
        arr.forEach(e => { map[e.id] = normalizePayMethod(e.paymentMethod || e.defaultPaymentMethod || ''); });
        return map;
    }, [employeesRaw]);

    // Holidays that fall within this pay period
    const holidaysInPeriod = useMemo(() => {
        if (!startDate || !endDate || !Array.isArray(companyHolidays)) return [];
        return companyHolidays.filter(h => {
            if (!h.isActive) return false;
            const d = h.date?.slice(0, 10);
            return d >= startDate && d <= endDate;
        });
    }, [companyHolidays, startDate, endDate]);

    // Map: employeeId → { ptoHours, sickHours, holidayHours, ptoStrict, sickStrict, holStrict }
    // *Strict = at least one policy of that type disallows negative balance
    const balanceByEmployee = useMemo(() => {
        const map = {};
        timeOffBalancesRaw.forEach(b => {
            if (!b.employeeId) return;
            if (!map[b.employeeId]) map[b.employeeId] = {
                ptoHours: 0, sickHours: 0, holidayHours: 0,
                ptoStrict: false, sickStrict: false, holStrict: false,
            };
            const type = (b.policy?.timeOffType || '').toLowerCase();
            const hrs = b.balanceHours ?? 0;
            const strict = b.policy?.allowNegativeBalance === false;
            if (type === 'sick') {
                map[b.employeeId].sickHours += hrs;
                if (strict) map[b.employeeId].sickStrict = true;
            } else if (type === 'holiday') {
                map[b.employeeId].holidayHours += hrs;
                if (strict) map[b.employeeId].holStrict = true;
            } else {
                map[b.employeeId].ptoHours += hrs;
                if (strict) map[b.employeeId].ptoStrict = true;
            }
        });
        return map;
    }, [timeOffBalancesRaw]);

    // Fetch approved timecards for this payroll period
    const { data: timecards = [] } = useQuery({
        queryKey: ['preview-timecards', startDate, endDate],
        queryFn: async () => {
            const cId = localStorage.getItem('companyId') || '';
            const res = await axiosInstance.get(`/companies/${cId}/timecards`, {
                params: { status: 'approved', periodStart: startDate, periodEnd: endDate }
            });
            return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        },
        enabled: !!startDate && !!endDate,
        staleTime: 0,
    });

    const approvedTimecardEmployeeIds = useMemo(
        () => new Set(timecards.map(t => t.employeeId)),
        [timecards]
    );

    // Sum approved timecard hours per employee for the pay period
    const timecardHoursByEmployee = useMemo(() => {
        const map = {};
        timecards.forEach(t => {
            if (!t.employeeId) return;
            map[t.employeeId] = (map[t.employeeId] || 0) + Number(t.totalHours || 0);
        });
        return map;
    }, [timecards]);

    // Fetch contractors to get payType for each
    const { data: contractorsList = [] } = useQuery({
        queryKey: ['contractors'],
        queryFn: getContractors,
        staleTime: 5 * 60 * 1000,
    });
    const payTypeMap = useMemo(() => {
        const map = {};
        contractorsList.forEach(c => { map[c.id] = c.payType || 'Hourly'; });
        return map;
    }, [contractorsList]);

    // Always filter to only approved timecard employees when timecards exist
    const filterByTimecards = approvedTimecardEmployeeIds.size > 0;

    // Employees to show in both breakdown table AND pre-approval adjustments
    const displayEmployees = useMemo(() => {
        const all = calc?.employees ?? [];
        if (!filterByTimecards) return all;
        // Show all salaried; show only hourly employees with approved timecards
        return all.filter(emp =>
            (emp.payType ?? '').toLowerCase() !== 'hourly' ||
            approvedTimecardEmployeeIds.has(emp.employeeId)
        );
    }, [calc, filterByTimecards, approvedTimecardEmployeeIds]);

    const timeOffByEmployee = useMemo(() => {
        const requests = Array.isArray(timeOffRaw) ? timeOffRaw : [];
        const map = {};
        requests.forEach(r => {
            if (!map[r.employeeId]) map[r.employeeId] = {
                ptoHours: 0, sickHours: 0, holidayHours: 0,
                ptoPaidHours: 0, sickPaidHours: 0, holPaidHours: 0,
                requestIds: [],
            };
            const field = typeToField(r.policy?.timeOffType);
            map[r.employeeId][field] = (map[r.employeeId][field] || 0) + Number(r.totalHours);
            // paidHours stored at approval time — use it to know how many were within balance
            const paidField = field === 'ptoHours' ? 'ptoPaidHours' : field === 'sickHours' ? 'sickPaidHours' : 'holPaidHours';
            const paidHrs = r.paidHours != null ? Number(r.paidHours) : Number(r.totalHours);
            map[r.employeeId][paidField] = (map[r.employeeId][paidField] || 0) + paidHrs;
            map[r.employeeId].requestIds.push(r.id);
        });
        return map;
    }, [timeOffRaw]);

    useEffect(() => {
        if (!calc?.employees) return;
        const newEntries = {};
        const included = new Set();
        const lateFillHolidayHrs = {};
        calc.employees.forEach(emp => {
            included.add(emp.employeeId);
            const isSalaried = (emp.payType ?? '').toLowerCase() !== 'hourly';

            // Calculate scheduled holiday hours BEFORE the initialized check
            // so we can retroactively fill if holidays loaded after employee data
            const scheduledHolidayHrs = holidaysInPeriod
                .filter(h => h.appliesTo === 'all' || isSalaried)
                .reduce((sum, h) => sum + Number(h.hours || 0), 0);

            // Skip employees already initialized — prevents double-counting after recalculate
            // But track holiday hours for late-fill if holidays just became available
            if (initializedEmpRef.current.has(emp.employeeId)) {
                if (scheduledHolidayHrs > 0) lateFillHolidayHrs[emp.employeeId] = scheduledHolidayHrs;
                return;
            }
            initializedEmpRef.current.add(emp.employeeId);

            const to = timeOffByEmployee[emp.employeeId] || {};

            // Use saved emp value if non-zero (already includes time off), else fall back to timeoff/holiday data
            const ptoHrs  = ((emp.ptoHours ?? 0) || (to.ptoHours || 0)) || null;
            const sickHrs = ((emp.sickHours ?? 0) || (to.sickHours || 0)) || null;
            const holHrs  = ((emp.paidHolidayHours ?? 0) || (to.holidayHours || 0) || scheduledHolidayHrs) || null;

            // For hourly employees: use approved timecard total hours, split into regular + overtime
            let regHrs = emp.regularHours ?? null;
            let otHrs  = emp.overtimeHours ?? null;
            if (!isSalaried) {
                const tcTotal = timecardHoursByEmployee[emp.employeeId] || 0;
                if (tcTotal > 0) {
                    // Split at 40 hrs/week threshold based on pay period length
                    const periodDays = startDate && endDate
                        ? Math.max(1, (new Date(endDate) - new Date(startDate)) / 86400000 + 1)
                        : 14;
                    const regThreshold = Math.round((periodDays / 7) * 40);
                    regHrs = Math.min(tcTotal, regThreshold);
                    otHrs  = Math.max(0, tcTotal - regThreshold) || null;
                }
            }

            // Auto-detect unpaid leave for salaried employees ONLY for strict policies
            // Uses paidHours stored on the request at approval time for accuracy
            let unpaidHours = null;
            if (isSalaried) {
                const bal = balanceByEmployee[emp.employeeId] || {
                    ptoStrict: false, sickStrict: false, holStrict: false,
                };
                const toData = timeOffByEmployee[emp.employeeId] || {};
                // excess = total hours - paid hours (paid hours = hours within balance at approval)
                const excessPto     = bal.ptoStrict  ? Math.max(0, (toData.ptoHours  || 0) - (toData.ptoPaidHours || toData.ptoHours  || 0)) : 0;
                const excessSick    = bal.sickStrict ? Math.max(0, (toData.sickHours || 0) - (toData.sickPaidHours || toData.sickHours || 0)) : 0;
                const excessHoliday = bal.holStrict  ? Math.max(0, (toData.holidayHours || 0) - (toData.holPaidHours || toData.holidayHours || 0)) : 0;
                const total = excessPto + excessSick + excessHoliday;
                if (total > 0) unpaidHours = total;
            }

            newEntries[emp.employeeId] = {
                hours: regHrs,
                ptoHours:     ptoHrs,
                sickHours:    sickHrs,
                holidayHours: holHrs,
                overtimeHours: otHrs,
                bonus: emp.bonusAmount ?? null,
                commission: emp.commissionAmount ?? null,
                earningCodeEarnings: emp.earningCodeEarnings ?? [],
                reimbursement: emp.reimbursementAmount ?? null,
                paymentMethod: normalizePayMethod(emp.paymentMethod) || employeePayMethodMap[emp.employeeId] || '',
                unpaidHours,
            };
        });
        if (Object.keys(newEntries).length > 0) {
            setAdjustments(prev => ({ ...prev, ...newEntries }));
        }
        // Late-fill holiday hours for already-initialized employees whose
        // holidayHours were null because holidays loaded after employee data
        if (Object.keys(lateFillHolidayHrs).length > 0) {
            setAdjustments(prev => {
                const updated = { ...prev };
                Object.entries(lateFillHolidayHrs).forEach(([empId, hrs]) => {
                    if (!updated[empId]?.holidayHours) {
                        updated[empId] = { ...(updated[empId] || {}), holidayHours: hrs };
                    }
                });
                return updated;
            });
        }
        setIncludedEmployeeIds(included);
    }, [calc, timeOffByEmployee, balanceByEmployee, holidaysInPeriod, employeePayMethodMap, timecardHoursByEmployee, startDate, endDate]);

    // Auto-apply adjustments & recalculate once when unpaid hours are auto-detected
    // so the Employee Breakdown shows the correct gross without manual interaction
    useEffect(() => {
        if (autoRecalcFiredRef.current) return;
        if (Object.keys(adjustments).length === 0) return;
        const hasUnpaid = Object.values(adjustments).some(adj => (adj.unpaidHours ?? 0) > 0);
        if (!hasUnpaid) return;
        autoRecalcFiredRef.current = true;
        // Small delay to ensure all state has settled before sending
        const t = setTimeout(() => applyAdj(), 600);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adjustments]);

    useEffect(() => {
        if (!calc?.contractors) return;
        const init = {};
        const included = new Set();
        calc.contractors.forEach(c => {
            init[c.contractorId] = {
                hours: c.hours ?? null,
                payRate: c.payRate ?? null,
                reimbursement: c.reimbursement ?? null,
                paymentMethod: normalizePayMethod(c.paymentMethod),
            };
            // Only pre-include contractors that have actual pay (were selected when payroll was created)
            if ((c.grossPay ?? 0) > 0 || (c.reimbursement ?? 0) > 0) {
                included.add(c.contractorId);
            }
        });
        setContAdjustments(init);
        setIncludedContractorIds(included);
    }, [calc]);

    const setAdj = (empId, field, val) =>
        setAdjustments(prev => ({ ...prev, [empId]: { ...prev[empId], [field]: val } }));

    const setContAdj = (cId, field, val) =>
        setContAdjustments(prev => ({ ...prev, [cId]: { ...prev[cId], [field]: val } }));

    const buildUpdatePayload = () => {
        const is1099Only = payroll?.type === '1099_only';
        // For 1099-only payrolls, preserve any W-2 employee items that were added
        // via the W-2 modal — send them through so they aren't wiped on recalculate
        const employeeItems = is1099Only
            ? (calc?.employees ?? []).map(e => ({
                employeeId: e.employeeId,
                paymentMethod: e.paymentMethod || undefined,
                payments: [{ regularHours: e.regularHours ?? undefined }],
                reimbursements: (e.reimbursementAmount ?? 0) > 0
                    ? [{ amount: e.reimbursementAmount, description: 'Reimbursement' }]
                    : undefined,
            }))
            : Object.entries(adjustments)
            .filter(([empId]) => includedEmployeeIds.has(empId))
            .map(([empId, adj]) => ({
            employeeId: empId,
            paymentMethod: adj.paymentMethod || undefined,
            payments: [{
                regularHours: adj.hours ?? undefined,
                ptoHours: adj.ptoHours ?? undefined,
                sickHours: adj.sickHours ?? undefined,
                paidHolidayHours: adj.holidayHours ?? undefined,
                overtimeHours: adj.overtimeHours ?? undefined,
                bonus: adj.bonus ?? undefined,
                commission: adj.commission ?? undefined,
                unpaidLeaveHours: adj.unpaidHours ?? undefined,
                doubleOvertimeHours: (() => {
                    const v = (adj.earningCodeEarnings || []).filter(e => e.type === 'DoubleOvertime').reduce((s, e) => s + (Number(e.value) || 0), 0);
                    return v > 0 ? v : undefined;
                })(),
                severance: (() => {
                    const v = (adj.earningCodeEarnings || []).filter(e => e.type === 'Severance').reduce((s, e) => s + (Number(e.value) || 0), 0);
                    return v > 0 ? v : undefined;
                })(),
                earningCodeEarnings: (adj.earningCodeEarnings || [])
                    .filter(e => e.type === 'Other' && e.earningCodeId && (e.value ?? 0) > 0)
                    .map(e => ({ earningCodeId: e.earningCodeId, value: Number(e.value) })),
            }],
            reimbursements: adj.reimbursement > 0
                ? [{ amount: adj.reimbursement, description: 'Reimbursement' }]
                : undefined,
        }));
        const contractorItems = Object.entries(contAdjustments)
            .filter(([cId]) => includedContractorIds.has(cId))
            .map(([cId, adj]) => ({
                contractorId: cId,
                hours: adj.hours ?? undefined,
                payRate: adj.payRate ?? undefined,
                reimbursement: adj.reimbursement ?? undefined,
                paymentMethod: adj.paymentMethod || undefined,
            }));
        return { payrollId: payroll.id, employeeItems, contractorItems };
    };

    const { mutate: applyAdj, isPending: applyingAdj } = useMutation({
        mutationFn: () => updatePayroll(buildUpdatePayload()),
        onSuccess: () => {
            setAdjError('');
            setAdjSuccess(true);
            setTimeout(() => setAdjSuccess(false), 3000);
            queryClient.refetchQueries({ queryKey: ['payroll-preview', payroll.id] });
        },
        onError: (err) => {
            setAdjError(err?.response?.data?.message || err?.message || 'Failed to apply adjustments. Please try again.');
        },
    });

    const { mutate: approve, isPending: approving } = useMutation({
        mutationFn: async () => {
            await updatePayroll(buildUpdatePayload());
            return approvePayroll(payroll.id);
        },
        onError: (err) => {
            setApproveError(err?.response?.data?.message || err?.message || 'Failed to approve payroll. Please try again.');
        },
        onSuccess: async () => {
            setApproveError('');
            queryClient.invalidateQueries({ queryKey: ['payrolls'] });

            if (calc?.employees?.length > 0) {
                const employees = calc.employees.map(emp => ({
                    employeeId: emp.employeeId,
                    hoursWorked: emp.regularHours ?? emp.hours ?? 0,
                }));
                runAccruals({ companyId: companyId(), payPeriodStart: startDate, payPeriodEnd: endDate, employees })
                    .catch(() => {});

                // Notify each employee their paycheck is ready
                const periodLabel = `${startDate} – ${endDate}`;
                calc.employees.forEach(emp => {
                    createNotification({
                        recipientType: 'employee',
                        recipientId: emp.employeeId,
                        type: 'paycheck_ready',
                        title: 'Your Paycheck Is Ready',
                        message: `Payroll for ${periodLabel} has been approved. Net pay: $${(emp.netPay ?? 0).toFixed(2)}.`,
                        link: '/employee/paystubs',
                    }).catch(() => {});
                });
            }

            // Notify contractors
            if (calc?.contractors?.length > 0) {
                const periodLabel = `${startDate} – ${endDate}`;
                calc.contractors.forEach(c => {
                    if (!c.contractorId) return;
                    createNotification({
                        recipientType: 'contractor',
                        recipientId: c.contractorId,
                        type: 'paycheck_ready',
                        title: 'Your Payment Is Ready',
                        message: `Payroll for ${periodLabel} has been approved. Amount: $${(c.grossPay ?? 0).toFixed(2)}.`,
                        link: '/contractor-portal/payments',
                    }).catch(() => {});
                });
            }

            const allRequestIds = Object.values(timeOffByEmployee).flatMap(v => v.requestIds || []);
            allRequestIds.forEach(id => markRequestPaid(id).catch(() => {}));

            setApproved(true);
        },
    });

    return (
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                zIndex: 1050, display: 'flex', alignItems: 'flex-start',
                justifyContent: 'center', overflowY: 'auto', padding: '24px 16px',
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 960, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h5 className='mb-0 fw-semibold'>Payroll Preview</h5>
                        <div className='d-flex flex-wrap gap-3 mt-1' style={{ fontSize: 13 }}>
                            <span className='text-muted'>
                                <strong style={{ color: '#555' }}>Pay Period:</strong>{' '}
                                {payroll.startDate ? new Date(payroll.startDate).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                                {' '}&mdash;{' '}
                                {payroll.endDate ? new Date(payroll.endDate).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                            </span>
                            {payroll.payDate && (
                                <span className='text-muted'>
                                    <strong style={{ color: '#555' }}>Pay Date:</strong>{' '}
                                    {new Date(payroll.payDate).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                            )}
                        </div>
                        <p className='mb-0 description mt-1' style={{ fontSize: 12, color: '#999' }}>
                            Review calculations before approving. FITW uses IRS bracket method.
                        </p>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>x</button>
                </div>

                <div style={{ padding: '24px' }}>
                    {isLoading && <div className='text-center py-5'>Calculating payroll...</div>}
                    {isError && <div className='text-center py-5 text-danger'>Failed to load preview.</div>}

                    {calc && !approved && (
                        <>
                            <div className='row g-3 mb-4'>
                                {[
                                    { label: 'Gross Pay', value: calc.totalGrossPay, color: '#198754' },
                                    { label: 'Deductions', value: calc.totalDeductions, color: '#dc3545' },
                                    { label: 'Total Taxes', value: calc.totalTaxes, color: '#1B75F0' },
                                    { label: 'Net Pay', value: calc.totalNetPay, color: '#0d6efd' },
                                ].map(card => (
                                    <div key={card.label} className='col-6 col-md-3'>
                                        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', borderLeft: `4px solid ${card.color}` }}>
                                            <p className='mb-1 description' style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>{card.label}</p>
                                            <p className='mb-0 fw-bold' style={{ fontSize: 18, color: card.color }}>{fmt(card.value)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {payroll?.type === '1099_only' && (!calc.contractors || calc.contractors.length === 0) && (
                                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                                    <p className='mb-0 fw-semibold' style={{ color: '#856404' }}>No contractors added yet.</p>
                                    <p className='mb-0' style={{ color: '#856404', fontSize: 13, marginTop: 4 }}>
                                        Close this preview and click <strong>"Add/Update Contractors"</strong> to add contractors before approving.
                                    </p>
                                </div>
                            )}

                            {(calc.contractors?.length > 0 || payroll?.type === '1099_only') && (
                                <>
                                    <h6 className='fw-semibold mb-3 mt-4'>
                                        Contractor Breakdown ({calc.contractors.filter(c => includedContractorIds.has(c.contractorId)).length} contractors)
                                    </h6>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table className='table table-sm table-hover' style={{ fontSize: 13, minWidth: 600 }}>
                                            <thead style={{ background: '#f8f9fa' }}>
                                                <tr>
                                                    <th className='ps-2'>Contractor</th>
                                                    <th className='text-end'>Hours</th>
                                                    <th className='text-end'>Pay Rate</th>
                                                    <th className='text-end'>Reimbursement</th>
                                                    <th className='text-end'>Payment Method</th>
                                                    <th className='text-end pe-2'>Gross Pay</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {calc.contractors
                                                    .filter(c => includedContractorIds.has(c.contractorId))
                                                    .map((c) => (
                                                    <tr key={c.contractorId}>
                                                        <td className='ps-2 fw-medium'>{c.firstName} {c.lastName}{c.businessName ? ` (${c.businessName})` : ''}</td>
                                                        <td className='text-end'>
                                                            {(() => {
                                                                const pt = payTypeMap[c.contractorId] || 'Hourly';
                                                                if (pt === 'Hourly') return c.hours ?? 0;
                                                                const labels = { Weekly: 'Weekly', Monthly: 'Monthly', FlatFee: 'Flat Fee' };
                                                                const colors = { Weekly: '#e65100', Monthly: '#2e7d32', FlatFee: '#880e4f' };
                                                                return <span style={{ fontSize: 11, fontWeight: 600, color: colors[pt] || '#555' }}>{labels[pt] || pt}</span>;
                                                            })()}
                                                        </td>
                                                        <td className='text-end'>{c.payRate != null ? fmt(c.payRate) : '-'}</td>
                                                        <td className='text-end'>{c.reimbursement > 0 ? fmt(c.reimbursement) : '-'}</td>
                                                        <td className='text-end'>{c.paymentMethod ?? '-'}</td>
                                                        <td className='text-end pe-2 fw-semibold text-success'>
                                                            {fmt((c.grossPay ?? (c.hours * c.payRate)) + (c.reimbursement > 0 ? c.reimbursement : 0))}
                                                            {c.reimbursement > 0 && (
                                                                <div style={{ fontSize: 10, fontWeight: 400, color: '#888', lineHeight: 1.2 }}>
                                                                    {fmt(c.grossPay ?? 0)} + {fmt(c.reimbursement)} reimb.
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className='text-end pe-2'>
                                                            <button
                                                                title='Remove from payroll'
                                                                onClick={() => setIncludedContractorIds(prev => { const s = new Set(prev); s.delete(c.contractorId); return s; })}
                                                                style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}
                                                            >×</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {(payroll?.type !== '1099_only' || (calc?.employees?.length > 0)) && (() => {
                                return (
                                    <>
                                        <div className='d-flex align-items-center justify-content-between mb-3 mt-4 flex-wrap gap-2'>
                                            <h6 className='fw-semibold mb-0'>
                                                Employee Breakdown ({displayEmployees.filter(e => includedEmployeeIds.has(e.employeeId)).length} employee{displayEmployees.filter(e => includedEmployeeIds.has(e.employeeId)).length !== 1 ? 's' : ''})
                                                {filterByTimecards && approvedTimecardEmployeeIds.size < (calc.employees?.length ?? 0) && (
                                                    <span style={{ fontSize: 12, fontWeight: 400, color: '#888', marginLeft: 8 }}>
                                                        ({(calc.employees?.length ?? 0) - approvedTimecardEmployeeIds.size} hidden — no approved timecards)
                                                    </span>
                                                )}
                                            </h6>
                                        </div>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table className='table table-sm table-hover' style={{ fontSize: 13, minWidth: 860 }}>
                                                <thead style={{ background: '#f8f9fa' }}>
                                                    <tr>
                                                        <th className='ps-2'>Employee</th>
                                                        <th>Type</th>
                                                        <th className='text-end'>Gross</th>
                                                        <th className='text-end'>Fed. Tax</th>
                                                        <th className='text-end'>State Tax</th>
                                                        <th className='text-end'>Soc. Sec.</th>
                                                        <th className='text-end'>Medicare</th>
                                                        <th className='text-end'>SDI</th>
                                                        <th className='text-end'>Add'l Med.</th>
                                                        <th className='text-end'>Local Tax</th>
                                                        <th className='text-end pe-2'>Net Pay</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {displayEmployees.filter(e => includedEmployeeIds.has(e.employeeId)).map((emp) => {
                                                        const hasTimecard = approvedTimecardEmployeeIds.has(emp.employeeId);
                                                        return (
                                                            <tr key={emp.employeeId} style={hasTimecard ? { background: '#fffbf5' } : {}}>
                                                                <td className='ps-2 fw-medium'>
                                                                    {emp.employeeFirstName} {emp.employeeLastName}
                                                                    {hasTimecard && (emp.payType ?? '').toLowerCase() === 'hourly' && (
                                                                        <span title='Has approved timecards' style={{ marginLeft: 6, fontSize: 11, background: '#fff3e0', color: '#1B75F0', border: '1px solid #ffd49e', borderRadius: 10, padding: '1px 6px', fontWeight: 600 }}>
                                                                            ⏱ Timecard
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td style={{ textTransform: 'capitalize' }}>{emp.payType}</td>
                                                                <td className='text-end'>{fmt(emp.grossPay)}</td>
                                                                <td className='text-end' style={{ color: '#1B75F0' }}>{emp.federalIncomeTax > 0 ? fmt(emp.federalIncomeTax) : '-'}</td>
                                                                <td className='text-end' style={{ color: '#1B75F0' }} title={emp.workStateCode ? `${emp.workStateCode} withholding: ${fmt(emp.workStateIncomeTax ?? 0)}` : undefined}>{emp.stateIncomeTax > 0 ? fmt(emp.stateIncomeTax) : '-'}{emp.workStateCode ? <span style={{ fontSize:9, marginLeft:3, opacity:0.7 }}>({emp.workStateCode})</span> : null}</td>
                                                                <td className='text-end' style={{ color: '#1B75F0' }}>{fmt(emp.socialSecurityTax)}</td>
                                                                <td className='text-end' style={{ color: '#1B75F0' }}>{fmt(emp.medicareTax)}</td>
                                                                <td className='text-end' style={{ color: '#1B75F0' }}>{(emp.sdiTax ?? 0) > 0 ? fmt(emp.sdiTax) : '-'}</td>
                                                                <td className='text-end' style={{ color: '#1B75F0' }}>{(emp.additionalMedicareTax ?? 0) > 0 ? fmt(emp.additionalMedicareTax) : '-'}</td>
                                                                <td className='text-end' style={{ color: '#1B75F0' }}>{(emp.localIncomeTax ?? 0) > 0 ? fmt(emp.localIncomeTax) : '-'}</td>
                                                                <td className='text-end pe-2 fw-semibold text-success'>{fmt(emp.netPay)}</td>
                                                                <td className='text-end pe-2'>
                                                                    <button
                                                                        title='Remove from payroll'
                                                                        onClick={() => setIncludedEmployeeIds(prev => { const s = new Set(prev); s.delete(emp.employeeId); return s; })}
                                                                        style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}
                                                                    >×</button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {displayEmployees.filter(e => includedEmployeeIds.has(e.employeeId)).length === 0 && (
                                                        <tr>
                                                            <td colSpan={13} className='text-center text-muted py-3' style={{ fontSize: 13 }}>
                                                                No employees included. Use Pre-Approval Adjustments to add employees back.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                );
                            })()}

                            {calc.employer && payroll?.type !== '1099_only' && (
                                <div className='mt-4 p-3' style={{ background: '#f8f9fa', borderRadius: 8 }}>
                                    <h6 className='fw-semibold mb-2' style={{ fontSize: 13 }}>Employer Tax Obligations <span style={{ fontWeight: 400, color: '#888' }}>(not deducted from employees)</span></h6>
                                    <div className='row g-2' style={{ fontSize: 13 }}>
                                        <div className='col-auto'>Social Security: <strong>{fmt(calc.employer.totalSS)}</strong></div>
                                        <div className='col-auto'>Medicare: <strong>{fmt(calc.employer.totalMed)}</strong></div>
                                        <div className='col-auto'>FUTA: <strong>{fmt(calc.employer.totalFuta)}</strong></div>
                                        {calc.employer.totalSuta > 0 && <div className='col-auto'>SUTA: <strong>{fmt(calc.employer.totalSuta)}</strong></div>}
                                        <div className='col-auto fw-semibold'>
                                            Total: <strong style={{ color: '#dc3545' }}>{fmt((calc.employer.totalSS || 0) + (calc.employer.totalMed || 0) + (calc.employer.totalFuta || 0) + (calc.employer.totalSuta || 0))}</strong>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className='mt-4'>
                                <button
                                    className='btn btn-sm btn-outline-secondary'
                                    onClick={() => setShowAdj(v => !v)}
                                    style={{ fontSize: 13 }}
                                >
                                    {showAdj ? 'Hide' : 'Show'} Pre-Approval Adjustments
                                </button>
                            </div>

                            {showAdj && (
                                <div className='mt-3 p-3' style={{ background: '#fffbf5', borderRadius: 8, border: '1px solid #ffe0b2' }}>
                                    <h6 className='fw-semibold mb-1' style={{ fontSize: 14 }}>Pre-Approval Adjustments</h6>
                                    <p style={{ fontSize: 12, color: '#888' }} className='mb-3'>Override hours, add PTO/Sick/Holiday pay, bonuses, or change payment methods before approving.</p>
                                    {holidaysInPeriod.length > 0 && (
                                        <div className='mb-2 px-3 py-2' style={{ background: '#e8f5e9', borderRadius: 6, fontSize: 12, color: '#1b5e20', border: '1px solid #a5d6a7' }}>
                                            🎉 <strong>{holidaysInPeriod.length} company holiday{holidaysInPeriod.length > 1 ? 's' : ''}</strong> detected in this pay period:&nbsp;
                                            {holidaysInPeriod.map(h => <strong key={h.id}>{h.name} ({h.hours}h)</strong>).reduce((a, b) => [a, ', ', b])}
                                            &nbsp;— Holiday hours auto-filled below.
                                        </div>
                                    )}
                                    {payroll?.type !== '1099_only' && Object.keys(timeOffByEmployee).length > 0 && (
                                        <div className='mb-3 px-3 py-2' style={{ background: '#e8f5e9', borderRadius: 6, fontSize: 12, color: '#2e7d32', border: '1px solid #c8e6c9' }}>
                                            <strong>{Object.keys(timeOffByEmployee).length} employee(s)</strong> have approved time off overlapping this pay period - hours auto-filled below. These will be automatically saved when you click Approve Payroll.
                                        </div>
                                    )}

                                    {payroll?.type !== '1099_only' && displayEmployees.length > 0 && (
                                        <>
                                            <div className='d-flex align-items-center justify-content-between mb-2'>
                                                <span className='fw-semibold' style={{ fontSize: 13 }}>Employees ({includedEmployeeIds.size} included)</span>
                                            </div>
                                            <div style={{ overflowX: 'auto' }}>
                                                <table className='table table-sm' style={{ fontSize: 12, minWidth: 900 }}>
                                                    <thead style={{ background: '#fff3e0' }}>
                                                        <tr>
                                                            <th style={{ minWidth: 140 }}>Employee</th>
                                                            <th className='text-center'>Reg. Hrs</th>
                                                            <th className='text-center' style={{ color: '#2196F3' }}>PTO Hrs</th>
                                                            <th className='text-center' style={{ color: '#9C27B0' }}>Sick Hrs</th>
                                                            <th className='text-center' style={{ color: '#4CAF50' }}>Holiday Hrs</th>
                                                            <th className='text-center' style={{ color: '#FF5722' }}>OT Hrs</th>
                                                            <th className='text-center' style={{ color: '#dc3545' }}>Unpaid Hrs</th>
                                                            <th className='text-center'>Bonus</th>
                                                            <th className='text-center'>Commission</th>
                                                            <th className='text-center'>Reimburse</th>
                                                            <th className='text-center' style={{ minWidth: 110 }}>Pay Method</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displayEmployees.filter(e => includedEmployeeIds.has(e.employeeId)).map(emp => {
                                                            const adj = adjustments[emp.employeeId] || {};
                                                            return (
                                                                <tr key={emp.employeeId}>
                                                                    <td className='fw-medium' style={{ fontSize: 12 }}>{emp.employeeFirstName} {emp.employeeLastName}{timeOffByEmployee[emp.employeeId] ? <span style={{ color: '#2196F3', fontSize: 10, marginLeft: 4 }}>PTO</span> : ''}</td>
                                                                    <td className='text-center'>
                                                                        {numInput(adj.hours, v => setAdj(emp.employeeId, 'hours', v))}
                                                                        {(emp.payType ?? '').toLowerCase() === 'hourly' && timecardHoursByEmployee[emp.employeeId] > 0 && (
                                                                            <div style={{ fontSize: 10, color: '#1B75F0', fontWeight: 600, marginTop: 2 }}>⏱ from timecards</div>
                                                                        )}
                                                                    </td>
                                                                    <td className='text-center'>{numInput(adj.ptoHours, v => setAdj(emp.employeeId, 'ptoHours', v))}</td>
                                                                    <td className='text-center'>{numInput(adj.sickHours, v => setAdj(emp.employeeId, 'sickHours', v))}</td>
                                                                    <td className='text-center'>{numInput(adj.holidayHours, v => setAdj(emp.employeeId, 'holidayHours', v))}</td>
                                                                    <td className='text-center'>{numInput(adj.overtimeHours, v => setAdj(emp.employeeId, 'overtimeHours', v))}</td>
                                                                    <td className='text-center'>
                                                                        {(emp.payType ?? '').toLowerCase() !== 'hourly' ? (
                                                                            <div style={{ position: 'relative' }}>
                                                                                {numInput(adj.unpaidHours, v => setAdj(emp.employeeId, 'unpaidHours', v), '0')}
                                                                                {(adj.unpaidHours > 0) && (
                                                                                    <span title='Exceeds available balance' style={{ position: 'absolute', top: -6, right: -6, background: '#dc3545', color: '#fff', borderRadius: '50%', width: 14, height: 14, fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>!</span>
                                                                                )}
                                                                            </div>
                                                                        ) : <span style={{ color: '#aaa', fontSize: 11 }}>N/A</span>}
                                                                    </td>
                                                                    <td className='text-center'>{numInput(adj.bonus, v => setAdj(emp.employeeId, 'bonus', v))}</td>
                                                                    <td className='text-center'>{numInput(adj.commission, v => setAdj(emp.employeeId, 'commission', v))}</td>
                                                                    <td className='text-center'>{numInput(adj.reimbursement, v => setAdj(emp.employeeId, 'reimbursement', v))}</td>
                                                                    <td className='text-center'>
                                                                        <select
                                                                            value={adj.paymentMethod || ''}
                                                                            onChange={e => setAdj(emp.employeeId, 'paymentMethod', e.target.value)}
                                                                            style={{ fontSize: 12, padding: '2px 4px', border: '1px solid #ced4da', borderRadius: 4, width: 100 }}
                                                                        >
                                                                            <option value=''>Default</option>
                                                                            <option value='check'>Check</option>
                                                                            <option value='direct_deposit'>Direct Deposit</option>
                                                                            <option value='manual'>Manual</option>
                                                                        </select>
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            title='Remove from payroll'
                                                                            onClick={() => setIncludedEmployeeIds(prev => { const s = new Set(prev); s.delete(emp.employeeId); return s; })}
                                                                            style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}
                                                                        >×</button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {includedEmployeeIds.size === 0 && (
                                                            <tr><td colSpan={12} className='text-center text-muted py-3' style={{ fontSize: 12 }}>No employees included in this payroll.</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}

                                    {payroll?.type !== '1099_only' && displayEmployees.length > 0 && (() => {
                                        // Only show earning codes not already handled in the standard table
                                        const STANDARD_TYPES = ['Regular', 'PTO', 'Holiday', 'Bonus', 'Commission', 'Reimbursement'];
                                        const customCodes = earningCodes.filter(c => !STANDARD_TYPES.includes(c.type));
                                        const TYPE_META = {
                                            DoubleOvertime: { label: 'Hours (2× rate)', isHours: true, color: '#e65100', hint: 'Enter hours — paid at 2× employee rate' },
                                            Severance:      { label: 'Amount ($)',      isHours: false, color: '#6a1b9a', hint: 'Flat dollar amount added to gross pay' },
                                            Other:          { label: 'Amount ($)',      isHours: false, color: '#1565c0', hint: 'Flat dollar amount added to gross pay' },
                                        };
                                        const includedEmps = displayEmployees.filter(e => includedEmployeeIds.has(e.employeeId));
                                        const hasAnyEntries = includedEmps.some(emp => (adjustments[emp.employeeId]?.earningCodeEarnings || []).length > 0);
                                        return (
                                            <div className='mt-4 p-3' style={{ background: '#f0f4ff', borderRadius: 8, border: '1px solid #c5cae9' }}>
                                                <div className='d-flex align-items-center justify-content-between mb-1'>
                                                    <div>
                                                        <h6 className='fw-semibold mb-0' style={{ fontSize: 13 }}>Custom Earning Code Line Items</h6>
                                                        <p style={{ fontSize: 11, color: '#888', margin: 0 }}>
                                                            <span style={{ color: '#e65100', fontWeight: 600 }}>Double Overtime</span> — enter hours, calculated at 2× rate &nbsp;|&nbsp;
                                                            <span style={{ color: '#6a1b9a', fontWeight: 600 }}>Severance / Other</span> — enter dollar amount
                                                        </p>
                                                    </div>
                                                </div>
                                                {hasAnyEntries && (
                                                    <table className='table table-sm mt-2 mb-1' style={{ fontSize: 12 }}>
                                                        <thead style={{ background: '#e8eaf6' }}>
                                                            <tr>
                                                                <th style={{ minWidth: 140 }}>Employee</th>
                                                                <th style={{ minWidth: 180 }}>Earning Code</th>
                                                                <th style={{ width: 130 }}>Value</th>
                                                                <th style={{ width: 40 }}></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {includedEmps.flatMap(emp =>
                                                                (adjustments[emp.employeeId]?.earningCodeEarnings || []).map((ec, idx) => {
                                                                    const meta = TYPE_META[ec.type] || TYPE_META.Other;
                                                                    return (
                                                                        <tr key={`${emp.employeeId}-ec-${idx}`}>
                                                                            <td className='fw-medium'>{emp.employeeFirstName} {emp.employeeLastName}</td>
                                                                            <td>
                                                                                <select
                                                                                    value={ec.earningCodeId || ''}
                                                                                    onChange={e => {
                                                                                        const selected = customCodes.find(c => c.id === e.target.value);
                                                                                        const updated = [...(adjustments[emp.employeeId]?.earningCodeEarnings || [])];
                                                                                        updated[idx] = { ...updated[idx], earningCodeId: e.target.value, name: selected?.name || '', type: selected?.type || 'Other' };
                                                                                        setAdj(emp.employeeId, 'earningCodeEarnings', updated);
                                                                                    }}
                                                                                    style={{ fontSize: 12, padding: '2px 4px', border: '1px solid #ced4da', borderRadius: 4, width: '100%' }}
                                                                                >
                                                                                    <option value=''>Select earning code...</option>
                                                                                    {customCodes.map(c => (
                                                                                        <option key={c.id} value={c.id}>{c.name}</option>
                                                                                    ))}
                                                                                </select>
                                                                                {ec.type && <div style={{ fontSize: 10, color: meta.color, marginTop: 2 }}>{meta.hint}</div>}
                                                                            </td>
                                                                            <td>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                                    <input
                                                                                        type='number'
                                                                                        min='0'
                                                                                        step={meta.isHours ? '0.5' : '0.01'}
                                                                                        placeholder={meta.isHours ? '0' : '0.00'}
                                                                                        value={ec.value ?? ''}
                                                                                        onChange={e => {
                                                                                            const updated = [...(adjustments[emp.employeeId]?.earningCodeEarnings || [])];
                                                                                            updated[idx] = { ...updated[idx], value: e.target.value === '' ? '' : parseFloat(e.target.value) };
                                                                                            setAdj(emp.employeeId, 'earningCodeEarnings', updated);
                                                                                        }}
                                                                                        style={{ width: 80, padding: '2px 6px', fontSize: 12, border: '1px solid #ced4da', borderRadius: 4, textAlign: 'right' }}
                                                                                    />
                                                                                    <span style={{ fontSize: 11, color: '#888' }}>{meta.isHours ? 'hrs' : '$'}</span>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <button
                                                                                    type='button'
                                                                                    onClick={() => {
                                                                                        const updated = (adjustments[emp.employeeId]?.earningCodeEarnings || []).filter((_, i) => i !== idx);
                                                                                        setAdj(emp.employeeId, 'earningCodeEarnings', updated);
                                                                                    }}
                                                                                    style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}
                                                                                >×</button>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                            )}
                                                        </tbody>
                                                    </table>
                                                )}
                                                {!hasAnyEntries && (
                                                    <p style={{ fontSize: 12, color: '#888', margin: '8px 0 4px' }}>No custom earnings added yet.</p>
                                                )}
                                                <div className='d-flex flex-wrap gap-2 mt-2'>
                                                    {includedEmps.map(emp => (
                                                        <button
                                                            key={emp.employeeId}
                                                            type='button'
                                                            onClick={() => setAdj(emp.employeeId, 'earningCodeEarnings', [
                                                                ...(adjustments[emp.employeeId]?.earningCodeEarnings || []),
                                                                { earningCodeId: '', name: '', type: '', value: '' },
                                                            ])}
                                                            style={{ fontSize: 11, padding: '3px 12px', border: '1px solid #3f51b5', borderRadius: 4, background: '#fff', color: '#3f51b5', cursor: 'pointer', fontWeight: 600 }}
                                                        >
                                                            + {emp.employeeFirstName} {emp.employeeLastName}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {calc.contractors?.length > 0 && (
                                        <div className='mt-3'>
                                            <div className='d-flex align-items-center justify-content-between mb-2' style={{ fontSize: 13 }}>
                                                <span className='fw-semibold'>Contractors ({includedContractorIds.size} included)</span>
                                                <div style={{ position: 'relative' }}>
                                                    <button
                                                        type='button'
                                                        onClick={() => setShowAddContractor(v => !v)}
                                                        style={{ fontSize: 12, padding: '4px 12px', background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                                                    >
                                                        + Add Contractor
                                                    </button>
                                                    {showAddContractor && (() => {
                                                        const notIncluded = calc.contractors.filter(c => !includedContractorIds.has(c.contractorId));
                                                        return (
                                                            <div style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 220, maxHeight: 240, overflowY: 'auto' }}>
                                                                {notIncluded.length === 0 ? (
                                                                    <div style={{ padding: '12px 16px', fontSize: 12, color: '#888' }}>All contractors are already included.</div>
                                                                ) : notIncluded.map(c => (
                                                                    <div
                                                                        key={c.contractorId}
                                                                        onClick={() => {
                                                                            setIncludedContractorIds(prev => new Set([...prev, c.contractorId]));
                                                                            // Pre-fill defaults if not already in adjustments
                                                                            setContAdjustments(prev => ({
                                                                                ...prev,
                                                                                [c.contractorId]: prev[c.contractorId] || {
                                                                                    hours: null, payRate: c.payRate ?? null,
                                                                                    reimbursement: null, paymentMethod: normalizePayMethod(c.paymentMethod) || 'check',
                                                                                },
                                                                            }));
                                                                            setShowAddContractor(false);
                                                                        }}
                                                                        style={{ padding: '9px 16px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                                        onMouseEnter={e => e.currentTarget.style.background = '#fffbf5'}
                                                                        onMouseLeave={e => e.currentTarget.style.background = ''}
                                                                    >
                                                                        <span className='fw-medium'>{c.firstName} {c.lastName}{c.businessName ? ` (${c.businessName})` : ''}</span>
                                                                        <span style={{ color: '#1B75F0', fontWeight: 700, marginLeft: 8 }}>+</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                            {includedContractorIds.size === 0 ? (
                                                <div style={{ fontSize: 12, color: '#888', padding: '10px 0' }}>No contractors included. Click <strong>+ Add Contractor</strong> to add one.</div>
                                            ) : (
                                                <table className='table table-sm' style={{ fontSize: 12 }}>
                                                    <thead style={{ background: '#e8f5e9' }}>
                                                        <tr>
                                                            <th style={{ minWidth: 140 }}>Contractor</th>
                                                            <th className='text-center'>Hours</th>
                                                            <th className='text-center'>Pay Rate</th>
                                                            <th className='text-center'>Reimburse</th>
                                                            <th className='text-center' style={{ minWidth: 140 }}>Pay Method</th>
                                                            <th style={{ width: 40 }}></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {calc.contractors
                                                            .filter(c => includedContractorIds.has(c.contractorId))
                                                            .map(c => {
                                                                const adj = contAdjustments[c.contractorId] || {};
                                                                return (
                                                                    <tr key={c.contractorId}>
                                                                        <td className='fw-medium' style={{ fontSize: 12 }}>{c.firstName} {c.lastName}</td>
                                                                        <td className='text-center'>
                                                                            {(() => {
                                                                                const pt = payTypeMap[c.contractorId] || 'Hourly';
                                                                                if (pt === 'Hourly') return numInput(adj.hours, v => setContAdj(c.contractorId, 'hours', v));
                                                                                const labels = { Weekly: 'Weekly', Monthly: 'Monthly', FlatFee: 'Flat Fee' };
                                                                                const colors = { Weekly: '#e65100', Monthly: '#2e7d32', FlatFee: '#880e4f' };
                                                                                return <span style={{ fontSize: 11, fontWeight: 600, color: colors[pt] || '#555', padding: '2px 6px', borderRadius: 4, background: '#f5f5f5', display: 'inline-block' }}>{labels[pt] || pt}</span>;
                                                                            })()}
                                                                        </td>
                                                                        <td className='text-center'>{numInput(adj.payRate, v => setContAdj(c.contractorId, 'payRate', v))}</td>
                                                                        <td className='text-center'>{numInput(adj.reimbursement, v => setContAdj(c.contractorId, 'reimbursement', v))}</td>
                                                                        <td className='text-center'>
                                                                            <select
                                                                                value={adj.paymentMethod || ''}
                                                                                onChange={e => setContAdj(c.contractorId, 'paymentMethod', e.target.value)}
                                                                                style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #ced4da', borderRadius: 4, width: 130 }}
                                                                            >
                                                                                <option value=''>Default</option>
                                                                                <option value='check'>Check</option>
                                                                                <option value='direct_deposit'>Direct Deposit</option>
                                                                                <option value='manual'>Manual</option>
                                                                            </select>
                                                                        </td>
                                                                        <td className='text-center'>
                                                                            <button
                                                                                title='Remove from payroll'
                                                                                onClick={() => setIncludedContractorIds(prev => { const s = new Set(prev); s.delete(c.contractorId); return s; })}
                                                                                style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                                                                            >×</button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}

                                    {payroll?.type !== '1099_only' && (
                                        <div className='mt-2 d-flex flex-wrap gap-3' style={{ fontSize: 11, color: '#666' }}>
                                            <span>Reg. Hrs = Regular hours worked</span>
                                            <span style={{ color: '#2196F3' }}>PTO Hrs = PTO / Vacation hours</span>
                                            <span style={{ color: '#9C27B0' }}>Sick Hrs = Sick leave hours</span>
                                            <span style={{ color: '#4CAF50' }}>Holiday Hrs = Paid holiday hours</span>
                                            <span style={{ color: '#FF5722' }}>OT Hrs = Overtime hours (1.5x)</span>
                                        </div>
                                    )}

                                    {adjError && (
                                        <div className='mt-3 p-2' style={{ background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 6, fontSize: 13, color: '#dc3545' }}>
                                            {adjError}
                                        </div>
                                    )}
                                    {adjSuccess && (
                                        <div className='mt-3 p-2' style={{ background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 6, fontSize: 13, color: '#2e7d32' }}>
                                            Adjustments applied - preview updated successfully.
                                        </div>
                                    )}

                                    <div className='mt-3 d-flex gap-2'>
                                        <button
                                            className='button-orange px-4'
                                            onClick={() => { setAdjError(''); setAdjSuccess(false); applyAdj(); }}
                                            disabled={applyingAdj}
                                            style={{ fontSize: 13 }}
                                        >
                                            {applyingAdj ? 'Applying...' : 'Apply Adjustments & Recalculate'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <p className='mt-3 mb-0' style={{ fontSize: 12, color: '#888' }}>
                                * FITW uses IRS Publication 15-T bracket method. SIT requires employee State to be set. PTO/Sick/Holiday hours are paid at the employee regular pay rate.
                            </p>
                        </>
                    )}

                    {approved && (
                        <div className='text-center py-5'>
                            <div style={{ fontSize: 48, color: '#198754' }}>✓</div>
                            <h5 className='fw-bold text-success mt-2'>Payroll Approved!</h5>
                            <p className='text-muted' style={{ fontSize: 14 }}>
                                The payroll has been approved and processed successfully.
                            </p>
                        </div>
                    )}
                </div>


                <div style={{ padding: '16px 24px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    {approved ? (
                        <>
                            <button className='cancel-button px-4' onClick={() => { onApproved?.(); onClose(); }}>Done</button>
                            <button
                                className='button-orange px-4'
                                onClick={() => { onApproved?.(); onClose(); navigate(`/dashboard/payroll/${payroll.id}`); }}
                            >
                                View Payroll Details
                            </button>
                        </>
                    ) : (
                        <>
                            {approveError && (
                                <span style={{ color: '#dc3545', fontSize: 13, alignSelf: 'center', marginRight: 'auto' }}>
                                    ⚠ {approveError}
                                </span>
                            )}
                            <button className='cancel-button px-4' onClick={onClose}>Cancel</button>
                            <button
                                className='button-orange px-5'
                                onClick={() => { setApproveError(''); approve(); }}
                                disabled={approving || applyingAdj || isLoading || !calc}
                            >
                                {approving ? 'Approving...' : 'Approve Payroll'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Bulk Holiday Modal ────────────────────────────────────────────────────────

export default PayrollPreviewModal;